import { RepaintProgress } from '../Gui/components/panels/ProgressBar';

import { SceneExporter } from './SceneExporter';
import { FREE_SCREEN_RECORDING_MAX_DURATION_SEC, VideoExporter } from './VideoExporter';
import { ScreenshotExporter, ScreenshotResolution } from './ScreenshotExporter';
import { DataExporter } from './DataExporter';
import useNotificationStore from '@gui/store/notification';
import { createAndUploadExportedFile, ExportedFileType } from '@api/exportedFileApi';
import { getGrid, useLoadingStore } from 'store/LoadingStore';
import { NotificationType } from '@visualizer/types';
import type { CoordinateSystemSelection } from '@visualizer/types';

import type { VideoExportSettings } from './VideoExporter';

// Block of the UI
import type { LoadingMessage } from '@gui/store/session/uiSessionStore';

// Restore of the camera
import { getViewer } from 'store/viewerStore';
import { getGlobalState } from '@gui/store/persistent/persistentStore';
import { CameraMode, type Camera } from '@visualizer/types';

export type ExportOptions = {
    variant: string;
    format: string;
    download: boolean;
    upload: boolean;
    individualMeshes: boolean;
    quality: number;
    easeInAnimation: boolean;
    coordinateSystem?: CoordinateSystemSelection;
    guiMode?: 'without' | 'with' | 'both';

    // Video options
    videoSettings?: Partial<VideoExportSettings>;

    // Orientation options for 3D export
    orientation3d?: 'Y_Up' | 'Z_Up';
};

export const defaultExportOptions: ExportOptions = {
    variant: '',
    format: '',
    download: true,
    upload: false,
    individualMeshes: false,
    quality: 100,
    easeInAnimation: false,
    orientation3d: 'Y_Up',
};

export const formatExportTimestamp = (): string => {
    const now = new Date();
    const pad = (value: number) => value.toString().padStart(2, '0');
    return `${pad(now.getDate())}-${pad(now.getMonth() + 1)}-${now.getFullYear()}_${pad(now.getHours())}:${pad(
        now.getMinutes()
    )}:${pad(now.getSeconds())}`;
};

export class ExportManager {
    // Free recording is the special case as we cannot block the ui while it is active
    private activeFreeRecording: {
        exporter: VideoExporter;
        options: ExportOptions;
        startedAtMs: number;
        cameraState: Camera | null;
        autoStopTimeoutId: number | null;
    } | null = null;

    private currentLoading: LoadingMessage | null = null;

    // Cancellation handling
    private activeCancel: (() => void) | null = null;

    private syncFreeScreenRecordingUi(startedAtMs: number | null) {
        useLoadingStore.getState().setFreeScreenRecording(
            startedAtMs == null
                ? null
                : {
                      active: true,
                      startedAtMs,
                      maxDurationSec: FREE_SCREEN_RECORDING_MAX_DURATION_SEC,
                  }
        );
    }

    private showCancellableLoading(message: LoadingMessage, onCancel: () => void) {
        this.activeCancel = onCancel;

        this.showLoading({
            ...message,
            cancellable: true,
            onCancel: () => {
                // UI feedback + prevent double cancel clicks
                this.showLoading({
                    ...this.currentLoading,
                    title: 'Cancelling…',
                    description: '',
                    cancellable: false,
                } as any);

                this.activeCancel?.();
            },
        });
    }

    constructor(private readonly setLoadingMessage?: (message: LoadingMessage | null) => void) {}

    private updateProgress(progress: number | undefined) {
        if (progress === undefined) {
            this.updateLoadingProgress(undefined);
            return;
        }

        // keep it 0..100 and integers only
        const p = Math.max(0, Math.min(100, Math.round(progress)));
        this.updateLoadingProgress(p);
    }

    private showLoading(message: LoadingMessage) {
        this.currentLoading = message;
        this.setLoadingMessage?.(message);
    }

    private hideLoading() {
        this.currentLoading = null;
        this.setLoadingMessage?.(null);
    }

    private updateLoadingProgress(progress?: number) {
        if (!this.currentLoading || progress === undefined) return;

        this.currentLoading = {
            ...this.currentLoading,
            progress,
        };

        this.setLoadingMessage?.(this.currentLoading);
    }

    async export3DModel(options: ExportOptions, selectedSPs?: Set<string>) {
        console.log(`Exporting 3D Model. Variant: ${options.variant}, Format: ${options.format}`);

        const grid = getGrid();
        const projectName = grid.name;
        const fileName = `3D-UM_${projectName}_${formatExportTimestamp()}`;

        console.log('Selected Layer/Frame IDs:', Array.from(selectedSPs || []));
        const exporter = new SceneExporter();
        exporter.options.exportIndividualBuildings = options.individualMeshes;
        exporter.options.exportIndividualTrees = options.individualMeshes;

        // Show loading with cancellable option
        this.showCancellableLoading({ title: 'Exporting 3D model…', description: 'You can cancel anytime.', progress: 0 }, () =>
            exporter.requestCancel()
        );

        await new Promise<void>((r) => window.requestAnimationFrame(() => r()));

        try {
            this.updateProgress(0);
            await RepaintProgress();
            let sceneData = await exporter.getSceneData(options.variant, selectedSPs);
            this.updateProgress(10);

            exporter.rotateToStandardCoordinateSpace(sceneData);
            sceneData = exporter.normalizeSceneDataFor3DExport(sceneData, options.orientation3d ?? 'Y_Up');
            exporter.applyCoordinateSystemTransform(sceneData, options.coordinateSystem, options.orientation3d ?? 'Y_Up');

            await RepaintProgress();
            const { formattedData, extension } = await exporter.getFormattedData(
                sceneData,
                options.format,
                fileName,
                (p) => this.updateProgress(p),
                options
            );
            if (formattedData.length > 0 || formattedData.byteLength > 0) {
                let format = options.format.toLowerCase();
                const fileExtension = extension ?? format;
                ExportManager.saveByteArray(
                    fileName,
                    this.map3dExportFormat(format),
                    formattedData,
                    options.download,
                    options.upload,
                    fileExtension
                );
            }
            this.updateProgress(undefined);
        } catch (e: any) {
            if (e?.message === 'SCENE_EXPORT_CANCELLED') return;
            throw e;
        } finally {
            this.hideLoading();
            this.updateProgress(undefined);
        }
    }

    private map3dExportFormat(variant: string): ExportedFileType {
        if (variant === 'gltf') return '3D_GLTF';
        if (variant === '3dm') return '3D_3DM';
        if (variant === 'obj') return '3D_OBJ';
        if (variant === 'stl') return '3D_STLF';
        if (variant === 'dxf') return '3D_DXF';

        throw Error('3D model export variant unknown');
    }

    private mapEportedVideoType(variant: string): ExportedFileType {
        if (variant === 'orbit') return 'ANIMATED_360_ORBIT';
        if (variant === 'lookaround') return 'ANIMATED_360_ORBIT';
        if (variant === 'timelapse') return 'ANIMATED_360_ORBIT';
        if (variant === 'screenrecording') return 'ANIMATED_360_ORBIT';

        throw Error('Animated export variant unknown');
    }

    async exportVideo(options: ExportOptions) {
        console.log(
            `Exporting Video. Variant: ${options.variant}, Format: ${options.format.toLowerCase()}, Quality: ${options.quality}, EaseIn: ${options.easeInAnimation}`
        );

        const exporter = new VideoExporter((p) => this.updateProgress(p));

        // Store the camera state
        const cameraState = this.captureCurrentCameraState();

        // Check the video mode
        const isFree = options.variant === 'screenrecording';

        // Block the UI only for non-free recording exports
        if (!isFree) {
            this.showLoading({
                title: 'Exporting video…',
                description: 'Capturing + encoding. You can cancel.',
                progress: 0,
                cancellable: true,
                onCancel: () => {
                    this.showLoading({ title: 'Cancelling…', description: 'Stopping encoder…' });
                    exporter.cancelEncoding(); // you already have this method in VideoExporter
                },
            });

            await new Promise<void>((r) => window.requestAnimationFrame(() => r()));
        }

        try {
            const videoData = await exporter.exportVideo(
                options.variant as any,
                options.format as any,
                options.quality,
                options.easeInAnimation,
                options.videoSettings
            );

            if (videoData.length === 0) {
                // cancelled
                return;
            }

            const grid = getGrid();
            const projectName = grid.name;
            const prefix = options.format.toLowerCase() === 'gif' ? 'Gif' : 'Video';
            const fileName = `${prefix}-UM_${projectName}_${formatExportTimestamp()}`;

            if (videoData.length > 0) {
                ExportManager.saveByteArray(
                    fileName,
                    this.mapEportedVideoType(options.variant),
                    videoData,
                    options.download,
                    options.upload,
                    options.format.toLowerCase()
                );
            }
        } finally {
            this.restoreCameraState(cameraState);

            if (!isFree) this.hideLoading();
            this.updateProgress(undefined);
        }
    }

    // Video free recording handling

    async startFreeScreenRecording(options: ExportOptions) {
        if (this.activeFreeRecording) return;

        const exporter = new VideoExporter(() => {});
        let startedAtMs = performance.now();

        try {
            // Start: NO loading (utente deve muoversi)
            startedAtMs = await exporter.beginFreeScreenRecording(
                options.format as any,
                options.quality,
                options.easeInAnimation,
                options.videoSettings,
                () => {
                    if (!this.activeFreeRecording) return;
                    useNotificationStore
                        .getState()
                        .showNotification('Browser sharing stopped. Finalizing recording.', NotificationType.Info, true);
                    void this.stopFreeScreenRecordingAndExport();
                }
            );
        } catch (error) {
            this.syncFreeScreenRecordingUi(null);
            console.error('Unable to start free screen recording', error);
            useNotificationStore
                .getState()
                .showNotification(
                    'Unable to start free screen recording. Check browser permissions and try again.',
                    NotificationType.Error,
                    true
                );
            throw error;
        }

        this.activeFreeRecording = {
            exporter,
            options,
            startedAtMs,
            cameraState: this.captureCurrentCameraState(),
            autoStopTimeoutId: window.setTimeout(() => {
                useNotificationStore
                    .getState()
                    .showNotification('Free recording limit reached. Exporting video.', NotificationType.Info, true);
                void this.stopFreeScreenRecordingAndExport();
            }, FREE_SCREEN_RECORDING_MAX_DURATION_SEC * 1000),
        };

        this.syncFreeScreenRecordingUi(startedAtMs);

        // durante recording progress bar la puoi lasciare undefined
        this.updateProgress(undefined);
    }

    async stopFreeScreenRecordingAndExport() {
        if (!this.activeFreeRecording) return;

        const { exporter, options, cameraState, autoStopTimeoutId } = this.activeFreeRecording;
        if (autoStopTimeoutId != null) {
            window.clearTimeout(autoStopTimeoutId);
        }
        this.activeFreeRecording = null;
        this.syncFreeScreenRecordingUi(null);

        // Ora blocchiamo: encoding è heavy
        this.showLoading({
            title: 'Encoding video…',
            description: 'Finalizing recording. You can cancel.',
            progress: 0,
            cancellable: true,
            onCancel: () => {
                this.showLoading({
                    ...this.currentLoading,
                    title: 'Cancelling…',
                    description: 'Stopping encoder…',
                    // keep progress + cancellable false to prevent double clicks
                    cancellable: false,
                });
                exporter.cancelEncoding();
            },
        });

        await new Promise<void>((r) => window.requestAnimationFrame(() => r()));

        try {
            const { data: videoData, extension } = await exporter.finishFreeScreenRecording();
            if (videoData.length === 0) return;

            if (extension !== options.format.toLowerCase()) {
                useNotificationStore
                    .getState()
                    .showNotification(
                        `Free screen recording was saved as .${extension} for browser compatibility.`,
                        NotificationType.Info,
                        true
                    );
            }

            const grid = getGrid();
            const projectName = grid.name;
            const prefix = 'Video';
            const fileName = `${prefix}-UM_${projectName}_${formatExportTimestamp()}`;

            if (videoData.length > 0) {
                ExportManager.saveByteArray(
                    fileName,
                    this.mapEportedVideoType(options.variant),
                    videoData,
                    options.download,
                    options.upload,
                    extension
                );
            }
        } catch (error) {
            console.error('Unable to export free screen recording', error);
            const message = error instanceof Error && error.message ? error.message : 'Unknown export error';
            useNotificationStore
                .getState()
                .showNotification(`Unable to export free screen recording. ${message}`, NotificationType.Error, true);
        } finally {
            this.restoreCameraState(cameraState);
            this.hideLoading();
            this.updateProgress(undefined);
        }
    }

    private captureCurrentCameraState(): Camera | null {
        const viewer = getViewer();
        if (!viewer) return null;

        const currentMode = getGlobalState().ambient.currentCamera.type as CameraMode;
        const { position, target } = viewer.cameraHelper.getCustomCamera(currentMode).getPositionAndTarget();

        return {
            type: currentMode,
            position: { ...position },
            target: { ...target },
        };
    }

    private restoreCameraState(cameraState: Camera | null) {
        if (!cameraState) return;

        const viewer = getViewer();
        if (!viewer) return;

        viewer.cameraHelper.setCameraMode(cameraState, cameraState.type, true);
    }

    async exportScreenshot(options: ExportOptions) {
        const variantToResolution: Record<string, ScreenshotResolution> = {
            screenres: ScreenshotResolution.ScreenRes,
            HD: ScreenshotResolution.HD,
            fullHD: ScreenshotResolution.FullHD,
            '4k': ScreenshotResolution.FourK,
            veryhighres: ScreenshotResolution.VeryHighRes,
        };

        console.log(`Exporting Screenshot. Variant: ${options.variant}, Format: ${options.format.toLowerCase()}`);
        const exporter = new ScreenshotExporter();
        const grid = getGrid();
        const projectName = grid.name;
        const baseFileName = `Sceenshot-UM_${projectName}_${formatExportTimestamp()}`;
        const resolution = variantToResolution[options.variant];
        const shouldCaptureWithGui = options.guiMode === 'with' || options.guiMode === 'both';
        const shouldCaptureWithoutGui = options.guiMode === 'without' || options.guiMode === 'both';

        if (shouldCaptureWithoutGui) {
            const screenshotData = await exporter.getScreenshot(resolution, false, options.format);
            if (screenshotData.length > 0) {
                ExportManager.saveByteArray(
                    `${baseFileName}_no-gui`,
                    'SCREENSHOT',
                    screenshotData,
                    options.download,
                    options.upload,
                    options.format.toLowerCase()
                );
            }
        }

        if (shouldCaptureWithGui) {
            const screenshotDataWithGui = await exporter.getScreenshot(resolution, true, options.format);
            if (screenshotDataWithGui.length > 0) {
                ExportManager.saveByteArray(
                    `${baseFileName}_with-gui`,
                    'SCREENSHOT',
                    screenshotDataWithGui,
                    options.download,
                    options.upload,
                    options.format.toLowerCase()
                );
            }
        }
        this.updateProgress(undefined);
    }

    private mapEportedTextType(variant: string): ExportedFileType {
        if (variant === 'CSV') return 'CSV_DATA';
        if (variant === 'TSV') return 'TSV_DATA';
        if (variant === 'JSON') return 'JSON_DATA';

        throw Error('Animated export variant unknown');
    }

    async exportTextData(options: ExportOptions, selectedSPs?: Set<string>) {
        console.log(`Exporting Text Data. Variant: ${options.variant}, Format: ${options.format.toLowerCase()}`);
        const exporter = new DataExporter();
        const data = exporter.getTextData(options.variant, options.format, selectedSPs, options.coordinateSystem);
        console.log('Exported Data:', data);
        let formattedData = exporter.getFormattedData(data, options.format);
        const grid = getGrid();
        const projectName = grid.name;
        const fileName = `Data-UM_${projectName}_${formatExportTimestamp()}`;
        ExportManager.saveTextFile(
            fileName,
            this.mapEportedTextType(options.format),
            formattedData,
            options.download,
            options.upload,
            options.format.toLowerCase()
        );
        this.updateProgress(undefined);
    }

    static saveByteArray(name: string, type: ExportedFileType, byte: any, download: boolean, upload: boolean, extension: string) {
        const mimeType = ExportManager.resolveMimeType(extension);
        let blob = new Blob([byte], { type: mimeType });
        if (upload) {
            createAndUploadExportedFile({ name, type }, blob)
                .then((response) => {
                    console.log('Data uploaded successfully:', response);
                })
                .catch((e) => {
                    useNotificationStore.getState().showNotification('Error contacting server', NotificationType.Error);
                    console.error(e);
                });
        }

        if (download) {
            let link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = name + '.' + extension;
            link.click();
            window.URL.revokeObjectURL(link.href);
            console.log('saving binary file...');
        }
    }

    static saveTextFile(name: string, type: ExportedFileType, content: string, download: boolean, upload: boolean, extension: string) {
        const mimeType = ExportManager.resolveMimeType(extension);
        let blob = new Blob([content], { type: mimeType });
        if (upload) {
            createAndUploadExportedFile({ name: name, type }, blob)
                .then((response) => {
                    console.log('Data uploaded successfully:', response);
                })
                .catch((e) => {
                    useNotificationStore.getState().showNotification('Error contacting server', NotificationType.Error);
                    console.error(e);
                });
        }

        if (download) {
            let link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = name + '.' + extension;
            link.click();
            window.URL.revokeObjectURL(link.href);
            console.log('saving text-file...');
        }
    }

    private static resolveMimeType(extension: string): string {
        const normalizedExtension = extension.replace(/^\./, '').toLowerCase();
        switch (normalizedExtension) {
            case 'png':
                return 'image/png';
            case 'jpg':
            case 'jpeg':
                return 'image/jpeg';
            case 'gif':
                return 'image/gif';
            case 'zip':
                return 'application/zip';
            case 'stl':
                return 'model/stl';
            case 'gltf':
                return 'model/gltf+json';
            case 'glb':
                return 'model/gltf-binary';
            case '3dm':
                return 'application/vnd.rhino.3d';
            case 'dxf':
                return 'image/vnd.dxf';
            case 'mp4':
                return 'video/mp4';
            case 'webm':
                return 'video/webm';
            case 'csv':
                return 'text/csv';
            case 'tsv':
                return 'text/tab-separated-values';
            case 'txt':
                return 'text/plain';
            case 'json':
            case 'geojson':
                return 'application/json';
            case 'xml':
                return 'application/xml';
            case 'yaml':
            case 'yml':
                return 'application/x-yaml';
            default:
                return 'application/octet-stream';
        }
    }
}
